import type { LearningProgressRepository } from '@/domain/precon-supporter/learning-progress';
import { LearningProgress } from '@/domain/precon-supporter/learning-progress';
import {
  CourseId,
  LectureProgress,
} from '@/domain/precon-supporter/value-objects';
import { indexedDBClient } from './IndexedDBClient';
import { STORE_NAMES, type DBSchema } from './schema';
import { sendGAEvent } from '@/utils/precon-supporter/analytics';

/**
 * IndexedDBを使った学習進捗リポジトリの実装
 */
export class IndexedDBLearningProgressRepository
  implements LearningProgressRepository
{
  /**
   * IDで学習進捗を取得
   */
  async findById(id: string): Promise<LearningProgress | null> {
    const store = await indexedDBClient.transaction(
      STORE_NAMES.LEARNING_PROGRESS,
      'readonly'
    );

    return new Promise((resolve, reject) => {
      const request = store.get(id);

      request.onsuccess = () => {
        const data = request.result;
        if (!data) {
          resolve(null);
          return;
        }

        resolve(this.toEntity(data));
      };

      request.onerror = () => {
        reject(new Error('学習進捗の取得に失敗しました'));
      };
    });
  }

  /**
   * コースIDで学習進捗を取得
   */
  async findByCourseId(courseId: CourseId): Promise<LearningProgress | null> {
    const store = await indexedDBClient.transaction(
      STORE_NAMES.LEARNING_PROGRESS,
      'readonly'
    );

    return new Promise((resolve, reject) => {
      const index = store.index('courseId');
      const request = index.get(courseId.toString());

      request.onsuccess = () => {
        const data = request.result;
        if (!data) {
          resolve(null);
          return;
        }

        resolve(this.toEntity(data));
      };

      request.onerror = () => {
        reject(new Error('学習進捗の取得に失敗しました'));
      };
    });
  }

  /**
   * すべての学習進捗を取得
   */
  async findAll(): Promise<LearningProgress[]> {
    const store = await indexedDBClient.transaction(
      STORE_NAMES.LEARNING_PROGRESS,
      'readonly'
    );

    return new Promise((resolve, reject) => {
      const request = store.getAll();

      request.onsuccess = () => {
        const dataList = request.result;
        const progressList = dataList.map(data => this.toEntity(data));
        resolve(progressList);
      };

      request.onerror = () => {
        reject(new Error('学習進捗の取得に失敗しました'));
      };
    });
  }

  /**
   * 学習進捗を保存
   */
  async save(learningProgress: LearningProgress): Promise<void> {
    const db = await indexedDBClient.open();
    const transaction = db.transaction(
      STORE_NAMES.LEARNING_PROGRESS,
      'readwrite'
    );
    const store = transaction.objectStore(STORE_NAMES.LEARNING_PROGRESS);

    return new Promise((resolve, reject) => {
      const data = learningProgress.toData();
      store.put(data);

      // トランザクションが完了するまで待つ
      // request.onsuccessはリクエストが成功したことを示すだけで、
      // トランザクションが完了したことを示すわけではない
      transaction.oncomplete = () => {
        resolve();
      };

      // request.onerrorはtransaction.onerrorに伝播するため、
      // transaction.onerrorのみでハンドルする
      transaction.onerror = () => {
        const error = new Error(
          `学習進捗の保存に失敗しました: ${transaction.error?.message || '不明なエラー'}`
        );
        reject(error);
        sendGAEvent('indexeddb_transaction_error', {
          error_message: transaction.error?.message || '不明なエラー',
          operation: 'save',
        });
      };
    });
  }

  /**
   * 学習進捗を削除
   */
  async delete(id: string): Promise<void> {
    const db = await indexedDBClient.open();
    const transaction = db.transaction(
      STORE_NAMES.LEARNING_PROGRESS,
      'readwrite'
    );
    const store = transaction.objectStore(STORE_NAMES.LEARNING_PROGRESS);

    return new Promise((resolve, reject) => {
      store.delete(id);

      // トランザクションが完了するまで待つ
      // request.onsuccessはリクエストが成功したことを示すだけで、
      // トランザクションが完了したことを示すわけではない
      transaction.oncomplete = () => {
        resolve();
      };

      // request.onerrorはtransaction.onerrorに伝播するため、
      // transaction.onerrorのみでハンドルする
      transaction.onerror = () => {
        const error = new Error(
          `学習進捗の削除に失敗しました: ${transaction.error?.message || '不明なエラー'}`
        );
        reject(error);
        sendGAEvent('indexeddb_transaction_error', {
          error_message: transaction.error?.message || '不明なエラー',
          operation: 'delete',
        });
      };
    });
  }

  /**
   * データからエンティティに変換
   * マイグレーション処理も含む
   */
  private toEntity(
    data: DBSchema['learningProgress']['value']
  ): LearningProgress {
    // lectureProgressesをLectureProgressオブジェクトに変換
    const lectureProgresses = data.lectureProgresses.map(p =>
      LectureProgress.reconstruct(
        p.lectureId,
        p.completed,
        p.completedAt ? new Date(p.completedAt) : null
      )
    );

    return LearningProgress.reconstruct({
      id: data.id,
      courseId: CourseId.fromString(data.courseId),
      lectureProgresses,
      examinationPassed: data.examinationPassed ?? false, // マイグレーション: 既存データはfalse
      createdAt: new Date(data.createdAt),
      updatedAt: new Date(data.updatedAt),
      passerToken: data.passerToken, // マイグレーション: 既存データはundefined
    });
  }
}
